import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Upload, Transform, CheckCircle } from '@mui/icons-material';
import apiService from '../utils/apiService';

const ConvertCartellas = () => {
  const [htmlData, setHtmlData] = useState('');
  const [branchId, setBranchId] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [availableIds, setAvailableIds] = useState({ branches: [], users: [] });
  const [loadingIds, setLoadingIds] = useState(false);

  const sampleData = `<tr><td style='font-size: 28px;'>1</td><td style='font-size: 18px;'>10,15,12,2,13</td><td style='font-size: 18px;'>18,23,16,17,24</td><td style='font-size: 18px;'>34,32,0,45,37</td><td style='font-size: 18px;'>46,53,47,60,52</td><td style='font-size: 18px;'>67,62,68,69,70</td><td style='font-size: 18px;'><a href='edit_card.php?card_no=1'><i class='fa fa-edit'></i></a></td></tr>
<tr><td style='font-size: 28px;'>2</td><td style='font-size: 18px;'>5,20,25,8,14</td><td style='font-size: 18px;'>19,28,21,29,30</td><td style='font-size: 18px;'>35,31,0,40,33</td><td style='font-size: 18px;'>50,55,48,58,59</td><td style='font-size: 18px;'>65,61,66,63,71</td><td style='font-size: 18px;'><a href='edit_card.php?card_no=2'><i class='fa fa-edit'></i></a></td></tr>`;

  const handleConvert = async () => {
    if (!htmlData.trim()) {
      setError('Please enter HTML data to convert');
      return;
    }

    if (!branchId.trim()) {
      setError('Please enter Branch ID');
      return;
    }

    if (!createdBy.trim()) {
      setError('Please enter Created By User ID');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await apiService.post('/convert', {
        htmlData,
        branchId,
        createdBy
      });

      setResult(response.data);
      setError('');
    } catch (error) {
      console.error('Convert error:', error);
      setError(error.response?.data?.error || 'Failed to convert data');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await apiService.get('/convert/test');
      setResult({
        ...response.data,
        success: true,
        message: 'Test conversion completed'
      });
    } catch (error) {
      console.error('Test error:', error);
      setError('Failed to run test conversion');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableIds = async () => {
    setLoadingIds(true);
    try {
      const response = await apiService.get('/convert/ids');
      console.log('Raw API Response:', response);
      
      // Handle different response formats
      let data;
      if (response.json && typeof response.json === 'function') {
        data = await response.json();
      } else if (response.data) {
        data = response.data;
      } else {
        data = response;
      }
      
      console.log('Parsed Data:', data);
      
      if (data && data.branches && data.users) {
        setAvailableIds(data);
        console.log('✅ Successfully loaded IDs:', {
          branches: data.branches.length,
          users: data.users.length
        });
      } else {
        console.error('Invalid API response structure:', data);
        setError('Invalid response from server - missing branches or users');
      }
    } catch (error) {
      console.error('Failed to load IDs:', error);
      setError(`Failed to load available IDs: ${error.message}`);
    } finally {
      setLoadingIds(false);
    }
  };

  const loadSampleData = () => {
    setHtmlData(sampleData);
    // Use Happy2 branch and its existing user
    setBranchId('b5be47e3-04a0-48c0-903c-1a897cd9c40c'); // Happy2 branch
    setCreatedBy('dd64ad80-f96e-4886-b027-7bd4e5b19e44'); // Existing user for Happy2
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Transform sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Convert Cartella Data
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" mb={3}>
          Convert HTML table data from PHP format to database-ready cartella objects.
        </Typography>

        <Box display="flex" gap={2} mb={2}>
          <Button
            variant="outlined"
            onClick={loadAvailableIds}
            disabled={loadingIds}
            size="small"
          >
            {loadingIds ? 'Loading...' : 'Load Available IDs'}
          </Button>
          
          {availableIds && availableIds.branches && availableIds.branches.length > 0 && (
            <Typography variant="body2" color="success.main" sx={{ alignSelf: 'center' }}>
              ✅ Found {availableIds.branches.length} branches, {availableIds.users.length} users
            </Typography>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {availableIds && availableIds.branches && availableIds.branches.length > 0 ? (
              <FormControl fullWidth margin="normal">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  label="Branch"
                >
                  {availableIds.branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label="Branch ID"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                placeholder="b5be47e3-04a0-48c0-903c-1a897cd9c40c"
                margin="normal"
                helperText="Click 'Load Available IDs' to see dropdown options"
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {availableIds && availableIds.users && availableIds.users.length > 0 ? (
              <FormControl fullWidth margin="normal">
                <InputLabel>Created By User</InputLabel>
                <Select
                  value={createdBy}
                  onChange={(e) => setCreatedBy(e.target.value)}
                  label="Created By User"
                >
                  {availableIds.users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label="Created By User ID"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                placeholder="dd64ad80-f96e-4886-b027-7bd4e5b19e44"
                margin="normal"
                helperText="Click 'Load Available IDs' to see dropdown options"
              />
            )}
          </Grid>
        </Grid>

        <TextField
          fullWidth
          multiline
          rows={8}
          label="HTML Table Data"
          value={htmlData}
          onChange={(e) => setHtmlData(e.target.value)}
          placeholder="Paste your HTML table rows here..."
          margin="normal"
          sx={{ mt: 2 }}
        />

        <Box display="flex" gap={2} mt={3} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
            onClick={handleConvert}
            disabled={loading}
          >
            {loading ? 'Converting...' : 'Convert & Save'}
          </Button>

          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={loading}
          >
            Test Conversion
          </Button>

          <Button
            variant="text"
            onClick={loadSampleData}
            disabled={loading}
          >
            Load Sample Data
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box mt={3}>
            <Alert 
              severity={result.success ? "success" : "info"} 
              icon={result.success ? <CheckCircle /> : undefined}
            >
              {result.message}
            </Alert>

            {result.cartellas && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Conversion Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Converted {result.count || result.cartellas.length} cartellas
                </Typography>
                
                {result.output && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample Output:
                    </Typography>
                    <pre style={{ 
                      fontSize: '12px', 
                      overflow: 'auto', 
                      maxHeight: '300px',
                      backgroundColor: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px'
                    }}>
                      {JSON.stringify(result.output?.[0] || result.cartellas?.[0], null, 2)}
                    </pre>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        )}

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Expected Input Format:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            HTML table rows with the following structure:
          </Typography>
          <pre style={{ 
            fontSize: '12px', 
            backgroundColor: '#f5f5f5', 
            padding: '10px', 
            borderRadius: '4px',
            overflow: 'auto'
          }}>
{`<tr>
  <td style='font-size: 28px;'>1</td>
  <td style='font-size: 18px;'>10,15,12,2,13</td>
  <td style='font-size: 18px;'>18,23,16,17,24</td>
  <td style='font-size: 18px;'>34,32,0,45,37</td>
  <td style='font-size: 18px;'>46,53,47,60,52</td>
  <td style='font-size: 18px;'>67,62,68,69,70</td>
  <td>...</td>
</tr>`}
          </pre>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConvertCartellas;
